// JoinSteps.tsx
import React from "react";
import { UserPlus, Wallet, ArrowRight } from "lucide-react";
import { DiscordIcon } from "./DiscordIcon"; // Import du composant que vous avez créé

interface JoinStepsProps {
  onOpenRegister: () => void;
}

export default function JoinSteps({ onOpenRegister }: JoinStepsProps) {
  // Tableau des étapes
  const steps = [
    {
      icon: UserPlus,
      title: "INSCRIS TOI",
      description: "Crée ton compte en quelques minutes et rejoins notre communauté de traders",
      gradient: "from-violet-600 to-indigo-600",
    },
    {
      icon: Wallet,
      title: "CRÉE TON COMPTE DE TRADING",
      description: "Crée ton compte de trading avec nos courtiers partenaires",
      gradient: "from-fuchsia-600 to-pink-600",
    },
    {
      // Remplacement de l’icône MessageSquare par DiscordIcon
      icon: DiscordIcon,
      title: "ACCÈDE AU DISCORD",
      description: "Rejoins notre communauté Discord exclusive de traders performants",
      gradient: "from-purple-600 to-violet-600",
    },
  ];

  return (
    <section id="cta-section" className="py-24 bg-black relative overflow-hidden">
      {/* Arrière-plan animé (gradients) */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-purple-900/20 via-black to-black" />
        <div className="absolute -top-24 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full filter blur-3xl animate-pulse" />
        <div className="absolute -bottom-24 right-1/4 w-96 h-96 bg-violet-600/10 rounded-full filter blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-20">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-400 text-lg font-semibold mb-4 block">
            COMMENT ÇA MARCHE
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            3 étapes simples pour commencer
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Rejoins notre communauté et commence à trader dès aujourd'hui
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8 mb-20">
          {steps.map((step, index) => (
            <button
              key={index}
              onClick={onOpenRegister}
              className="flex-1 group relative text-left"
            >
              {/* Barre de connexion entre les étapes (pour desktop) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-purple-500/50 to-transparent transform -translate-y-1/2" />
              )}

              {/* Card */}
              <div className="relative h-full">
                {/* Effet de halo gradien au survol */}
                <div className="absolute -inset-[2px] bg-gradient-to-r from-purple-600 via-violet-600 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-100 blur-sm transition-all duration-500" />

                <div className="relative h-full bg-black/80 backdrop-blur-sm p-8 rounded-2xl border border-purple-500/20 group-hover:border-purple-500/40 transition-all duration-500">
                  {/* Badge d'étape en haut à gauche */}
                  <div className="absolute -top-5 left-8 bg-black px-4 py-2 rounded-full">
                    <span
                      className={`text-transparent bg-clip-text bg-gradient-to-r ${step.gradient} font-bold text-lg`}
                    >
                      ÉTAPE {index + 1}
                    </span>
                  </div>

                  {/* Icône */}
                  <div className="relative w-16 h-16 mb-8 mt-4">
                    <div
                      className={`absolute inset-0 bg-gradient-to-r ${step.gradient} rounded-2xl blur transition-all duration-500 group-hover:blur-md opacity-75`}
                    />
                    <div className="relative h-full rounded-2xl bg-black p-4 flex items-center justify-center transform group-hover:scale-110 transition-all duration-500">
                      {/* Le composant icône (lucide-react ou DiscordIcon) */}
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Bouton d'appel à l'action */}
        <div className="flex justify-center">
          <button onClick={onOpenRegister} className="group relative">
            {/* Halo gradien au survol */}
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-violet-600 rounded-full blur opacity-75 group-hover:opacity-100 transition-all duration-500" />
            
            <div className="relative px-8 py-4 bg-black rounded-full flex items-center space-x-2 transform hover:scale-105 transition-all duration-500">
              <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-400">
                COMMENCER MAINTENANT
              </span>
              <ArrowRight className="w-5 h-5 text-purple-400 transform group-hover:translate-x-1 transition-transform duration-300" />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}
